import {
  formatDateTimeInTimeZone,
  formatLongDateInTimeZone,
  getDateTimePartsInTimeZone,
  resolveGigTimeZone,
  toMinutePrecision,
  type ZonedDateParts,
  zonedDateTimePartsToDate,
} from "./DateUtils";

export type PresetDueDateRange = "NEXT_FEW_DAYS" | "THIS_WEEKEND" | "THIS_WEEK";
export type PreferredTimePreset = "MORNING" | "AFTERNOON" | "EVENING" | "CUSTOM";

type TimeSlotRange = {
  startHour: number;
  endHour: number;
};

type ResolvedScheduleWindow = {
  startTime: Date;
  endTime: Date;
};

type ParsedSimpleScheduleNotes = {
  dueDateLabel: string;
  preferredTimeLabel: string | null;
  flexibleTimingDetails: string | null;
};

const NEXT_FEW_DAYS_OFFSET = 3;
const SIMPLE_WHEN_PREFIX = "Need this done:";
const SIMPLE_TIME_PREFIX = "Preferred time:";
const SIMPLE_FLEXIBLE_DETAILS_PREFIX = "Timing details:";

const TIME_SLOT_BY_PRESET: Record<Exclude<PreferredTimePreset, "CUSTOM">, TimeSlotRange> = {
  MORNING: {
    startHour: 8,
    endHour: 12,
  },
  AFTERNOON: {
    startHour: 12,
    endHour: 16,
  },
  EVENING: {
    startHour: 16,
    endHour: 20,
  },
};

const zonedDatePartsToUtcDate = (parts: ZonedDateParts) => new Date(Date.UTC(parts.year, parts.month - 1, parts.day));

const addDaysToZonedDateParts = (parts: ZonedDateParts, days: number): ZonedDateParts => {
  const date = zonedDatePartsToUtcDate(parts);
  date.setUTCDate(date.getUTCDate() + days);
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
};

const getZonedDayOfWeek = (parts: ZonedDateParts) => zonedDatePartsToUtcDate(parts).getUTCDay();

const withZonedTime = (
  parts: ZonedDateParts,
  hour: number,
  minute: number,
  second: number,
  millisecond: number,
  timeZone: string,
) => zonedDateTimePartsToDate({
  ...parts,
  hour,
  minute,
  second,
  millisecond,
}, timeZone);

const getWeekendBounds = (now: ZonedDateParts) => {
  const day = getZonedDayOfWeek(now);
  const saturday = day === 6 ? now : day === 0 ? addDaysToZonedDateParts(now, -1) : addDaysToZonedDateParts(now, 6 - day);
  const sunday = day === 6 ? addDaysToZonedDateParts(now, 1) : day === 0 ? now : addDaysToZonedDateParts(now, 7 - day);
  return { startDay: saturday, endDay: sunday };
};

const getThisWeekBounds = (now: ZonedDateParts) => {
  const day = getZonedDayOfWeek(now);
  const sunday = day === 0 ? now : addDaysToZonedDateParts(now, 7 - day);
  return { startDay: now, endDay: sunday };
};

const getNextFewDaysBounds = (now: ZonedDateParts) => ({
  startDay: now,
  endDay: addDaysToZonedDateParts(now, NEXT_FEW_DAYS_OFFSET),
});

const getPresetBounds = (dueDatePreset: PresetDueDateRange, now: ZonedDateParts) => {
  if (dueDatePreset === "THIS_WEEKEND") {
    return getWeekendBounds(now);
  }
  if (dueDatePreset === "THIS_WEEK") {
    return getThisWeekBounds(now);
  }
  return getNextFewDaysBounds(now);
};

const isSameDateInTimeZone = (startTime: string, endTime: string, timeZone?: string | null) => {
  const resolvedTimeZone = resolveGigTimeZone(timeZone);
  const startDate = getDateTimePartsInTimeZone(new Date(startTime), resolvedTimeZone);
  const endDate = getDateTimePartsInTimeZone(new Date(endTime), resolvedTimeZone);

  return Boolean(startDate && endDate)
    && startDate?.year === endDate?.year
    && startDate?.month === endDate?.month
    && startDate?.day === endDate?.day;
};

const parseTimeInputParts = (value: string) => {
  const [hoursText, minutesText] = value.split(":");
  const hours = Number(hoursText);
  const minutes = Number(minutesText);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return { hours, minutes };
};

const formatTimeParts = (hours: number, minutes: number) => {
  const suffix = hours >= 12 ? "pm" : "am";
  const normalizedHours = hours % 12 || 12;
  return minutes === 0
    ? `${normalizedHours}${suffix}`
    : `${normalizedHours}:${String(minutes).padStart(2, "0")}${suffix}`;
};

const formatTimeValue = (value: string) => {
  const parts = parseTimeInputParts(value);
  if (!parts) {
    return value;
  }

  return formatTimeParts(parts.hours, parts.minutes);
};

const formatPreferredTimeRange = (start: string, end: string) => `${formatTimeValue(start)} - ${formatTimeValue(end)}`;

const formatFixedScheduleLead = (startTime: string, endTime: string, timeZone?: string | null) =>
  isSameDateInTimeZone(startTime, endTime, timeZone)
    ? `Wants gig done on ${formatLongDateInTimeZone(startTime, timeZone)}.`
    : `Wants gig done sometime between ${formatLongDateInTimeZone(startTime, timeZone)} and ${formatLongDateInTimeZone(endTime, timeZone)}.`;

const formatPreferredTimeLabel = (value: string) => {
  const normalized = value.trim().toLowerCase();
  if (normalized === "8-12" || normalized === "8am - 12pm") {
    return "between 8am and 12pm";
  }
  if (normalized === "12-4" || normalized === "12pm - 4pm") {
    return "between 12pm and 4pm";
  }
  if (normalized === "4-8" || normalized === "4pm - 8pm") {
    return "between 4pm and 8pm";
  }
  return value.trim();
};

const toDate = (value: Date | string) => value instanceof Date ? value : new Date(value);

const getShortTimeZoneName = (value: Date | string, timeZone: string) => {
  const parsed = toDate(value);
  if (Number.isNaN(parsed.getTime())) {
    return timeZone;
  }

  const timeZoneName = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour: "numeric",
    timeZoneName: "short",
  }).formatToParts(parsed).find((part) => part.type === "timeZoneName")?.value;

  return timeZoneName ?? timeZone;
};

const formatTimeZoneRangeLabel = (startTime: Date | string, endTime: Date | string, timeZone: string) => {
  const startLabel = getShortTimeZoneName(startTime, timeZone);
  const endLabel = getShortTimeZoneName(endTime, timeZone);

  return startLabel === endLabel ? startLabel : `${startLabel}/${endLabel}`;
};

const preferredLocalTimeToDate = (timeValue: string, referenceTime: string, timeZone: string) => {
  const timeParts = parseTimeInputParts(timeValue);
  const referenceParts = getDateTimePartsInTimeZone(new Date(referenceTime), timeZone);
  if (!timeParts || !referenceParts) {
    return null;
  }

  return zonedDateTimePartsToDate({
    year: referenceParts.year,
    month: referenceParts.month,
    day: referenceParts.day,
    hour: timeParts.hours,
    minute: timeParts.minutes,
    second: 0,
    millisecond: 0,
  }, timeZone);
};

const formatPreferredTimeSentence = (
  preferredTimeLabel: string,
  startTime: string,
  endTime: string,
  timeZone: string,
  preferredStartTime?: string | null,
  preferredEndTime?: string | null,
) => {
  const fallbackStart = new Date(startTime);
  const fallbackEnd = new Date(endTime);
  const preferredStartDate = preferredStartTime ? preferredLocalTimeToDate(preferredStartTime, startTime, timeZone) : null;
  const preferredEndDate = preferredEndTime ? preferredLocalTimeToDate(preferredEndTime, startTime, timeZone) : null;
  const displayStart = preferredStartDate ?? fallbackStart;
  const displayEnd = preferredEndDate ?? fallbackEnd;
  const gigTimeLabel = `${formatPreferredTimeLabel(preferredTimeLabel)} ${formatTimeZoneRangeLabel(displayStart, displayEnd, timeZone)}`;

  return `Preferred time ${gigTimeLabel}.`;
};

export const parseSimpleScheduleNotes = (value: string | null): ParsedSimpleScheduleNotes | null => {
  if (!value) {
    return null;
  }

  const lines = value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
  const whenLine = lines.find((line) => line.startsWith(SIMPLE_WHEN_PREFIX));
  const flexibleDetailsLine = lines.find((line) => line.startsWith(SIMPLE_FLEXIBLE_DETAILS_PREFIX));
  if (!whenLine && !flexibleDetailsLine) {
    return null;
  }

  const timeLine = lines.find((line) => line.startsWith(SIMPLE_TIME_PREFIX));

  return {
    dueDateLabel: whenLine ? whenLine.slice(SIMPLE_WHEN_PREFIX.length).trim() : "",
    preferredTimeLabel: timeLine ? timeLine.slice(SIMPLE_TIME_PREFIX.length).trim() : null,
    flexibleTimingDetails: flexibleDetailsLine ? flexibleDetailsLine.slice(SIMPLE_FLEXIBLE_DETAILS_PREFIX.length).trim() : null,
  };
};

export const formatGigScheduleSummary = ({
  scheduleType,
  scheduleNotes,
  startTime,
  endTime,
  preferredStartTime,
  preferredEndTime,
  timeZone,
}: {
  scheduleType: "FIXED" | "FLEXIBLE";
  scheduleNotes: string | null;
  startTime: string | null;
  endTime: string | null;
  preferredStartTime?: string | null;
  preferredEndTime?: string | null;
  timeZone?: string | null;
}) => {
  const resolvedTimeZone = resolveGigTimeZone(timeZone);
  const parsedNotes = parseSimpleScheduleNotes(scheduleNotes);

  if (scheduleType === "FIXED" && startTime && endTime && parsedNotes) {
    const parts = [
      formatFixedScheduleLead(startTime, endTime, resolvedTimeZone),
    ];

    if (preferredStartTime && preferredEndTime) {
      parts.push(formatPreferredTimeSentence(
        formatPreferredTimeRange(preferredStartTime, preferredEndTime),
        startTime,
        endTime,
        resolvedTimeZone,
        preferredStartTime,
        preferredEndTime,
      ));
    } else if (parsedNotes.preferredTimeLabel) {
      parts.push(formatPreferredTimeSentence(parsedNotes.preferredTimeLabel, startTime, endTime, resolvedTimeZone));
    }

    return parts.join(" ");
  }

  if (scheduleType === "FIXED" && startTime && endTime && preferredStartTime && preferredEndTime) {
    return [
      formatFixedScheduleLead(startTime, endTime, resolvedTimeZone),
      formatPreferredTimeSentence(
        formatPreferredTimeRange(preferredStartTime, preferredEndTime),
        startTime,
        endTime,
        resolvedTimeZone,
        preferredStartTime,
        preferredEndTime,
      ),
    ].join(" ");
  }

  if (scheduleType === "FLEXIBLE" && parsedNotes?.flexibleTimingDetails) {
    return `Scheduling is flexible. ${parsedNotes.flexibleTimingDetails}`;
  }

  if (scheduleNotes?.trim()) {
    if (scheduleNotes.trim().startsWith(SIMPLE_FLEXIBLE_DETAILS_PREFIX)) {
      return scheduleNotes.trim().slice(SIMPLE_FLEXIBLE_DETAILS_PREFIX.length).trim();
    }
    return scheduleNotes.trim();
  }

  if (scheduleType === "FLEXIBLE") {
    return "Flexible scheduling";
  }

  if (startTime && endTime) {
    return `${formatDateTimeInTimeZone(startTime, resolvedTimeZone)} to ${formatDateTimeInTimeZone(endTime, resolvedTimeZone)}`;
  }

  return "Schedule to be confirmed";
};

export const resolvePresetScheduleWindow = (
  dueDatePreset: PresetDueDateRange,
  preferredTimePreset: PreferredTimePreset,
  now: Date,
  timeZone?: string | null,
): ResolvedScheduleWindow | null => {
  const resolvedTimeZone = resolveGigTimeZone(timeZone);
  const normalizedNow = toMinutePrecision(now);
  const nowParts = getDateTimePartsInTimeZone(normalizedNow, resolvedTimeZone);
  if (!nowParts) {
    return null;
  }

  const { startDay, endDay } = getPresetBounds(dueDatePreset, nowParts);
  const startBoundary =
    preferredTimePreset === "CUSTOM"
      ? withZonedTime(startDay, 0, 0, 0, 0, resolvedTimeZone)
      : withZonedTime(startDay, TIME_SLOT_BY_PRESET[preferredTimePreset].startHour, 0, 0, 0, resolvedTimeZone);
  const endBoundary =
    preferredTimePreset === "CUSTOM"
      ? withZonedTime(endDay, 23, 59, 59, 999, resolvedTimeZone)
      : withZonedTime(endDay, TIME_SLOT_BY_PRESET[preferredTimePreset].endHour, 0, 0, 0, resolvedTimeZone);
  if (!startBoundary || !endBoundary) {
    return null;
  }
  const clampedStart = startBoundary > normalizedNow ? startBoundary : normalizedNow;

  if (endBoundary <= clampedStart) {
    return null;
  }

  return {
    startTime: clampedStart,
    endTime: endBoundary,
  };
};
